import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  FileText, DollarSign, Package, Clock, ArrowLeft,
  CheckCircle, AlertTriangle, Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function BusinessLoanRequest() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [receivables, setReceivables] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedAR, setSelectedAR] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [form, setForm] = useState({
    amount: '',
    purpose: '',
    term_days: '30',
    preferred_stablecoin: '',
    additional_notes: ''
  });

  const headers = { Authorization: `Bearer ${token}` };

  const purposes = [
    'Payroll bridge',
    'Operational expenses',
    'Inventory purchase',
    'Equipment acquisition',
    'Business expansion',
    'Vendor payments',
    'Tax obligations',
    'Other'
  ];

  const stablecoins = [
    { value: 'USD', label: 'USD — US Dollar' },
    { value: 'USDC', label: 'USDC — USD Coin' },
    { value: 'USDT', label: 'USDT — Tether' },
    { value: 'DAI', label: 'DAI — MakerDAO' },
    { value: 'USAT', label: 'USAT — Tether U.S.' },
  ];

  useEffect(() => {
    const fetchCollateral = async () => {
      try {
        const [arRes, assetRes] = await Promise.all([
          axios.get(`${API}/business/receivables`, { headers }),
          axios.get(`${API}/business/assets`, { headers }),
        ]);
        setReceivables(arRes.data.receivables.filter(r => !r.pledged && r.status === 'active'));
        setAssets(assetRes.data.assets.filter(a => !a.pledged));
      } catch (err) {
        toast({ title: 'Error loading collateral', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };
    fetchCollateral();
  }, [token]);

  const toggleAR = (id) => {
    setSelectedAR(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleAsset = (id) => {
    setSelectedAssets(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const totalCollateral = [
    ...receivables.filter(r => selectedAR.includes(r.id)).map(r => r.invoice_amount),
    ...assets.filter(a => selectedAssets.includes(a.id)).map(a => a.estimated_value),
  ].reduce((sum, v) => sum + v, 0);

  const maxLoan = totalCollateral * 0.9;
  const advanceRate = form.amount && totalCollateral > 0
    ? (parseFloat(form.amount) / totalCollateral * 100).toFixed(1)
    : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedAR.length === 0 && selectedAssets.length === 0) {
      toast({ title: 'Select collateral', description: 'Pledge at least one receivable or asset.', variant: 'destructive' });
      return;
    }
    if (parseFloat(form.amount) > maxLoan) {
      toast({ title: 'Amount too high', description: `Max loan at 90% advance: $${maxLoan.toLocaleString()}`, variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/business/loan-requests`, {
        amount: parseFloat(form.amount),
        purpose: form.purpose,
        term_days: parseInt(form.term_days),
        pledged_receivable_ids: selectedAR,
        pledged_asset_ids: selectedAssets,
        preferred_stablecoin: form.preferred_stablecoin || null,
        additional_notes: form.additional_notes || null,
      }, { headers });
      toast({ title: 'Loan request submitted!', description: 'Lenders can now bid on your request.' });
      navigate('/business');
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to submit', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout title="New Business Loan Request">
      <div data-testid="business-loan-request-page">
        <Button variant="ghost" size="sm" onClick={() => navigate('/business')} className="mb-4 text-gray-400">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Dashboard
        </Button>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Loan Details */}
              <div className="p-6 rounded-xl bg-white/[0.03] border border-white/5">
                <h3 className="font-semibold mb-4">Loan Details</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Loan Amount ($)</Label>
                    <Input required type="number" step="0.01" min="100" value={form.amount}
                      onChange={(e) => setForm({...form, amount: e.target.value})}
                      className="mt-1 bg-white/5 border-white/10" placeholder="60000"
                      data-testid="loan-amount-input" />
                    {form.amount && parseFloat(form.amount) > maxLoan && maxLoan > 0 && (
                      <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Exceeds 90% advance rate. Max: ${maxLoan.toLocaleString()}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Purpose</Label>
                    <Select value={form.purpose} onValueChange={(v) => setForm({...form, purpose: v})}>
                      <SelectTrigger className="mt-1 bg-white/5 border-white/10" data-testid="purpose-select">
                        <SelectValue placeholder="Select purpose" />
                      </SelectTrigger>
                      <SelectContent>
                        {purposes.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Term (Days)</Label>
                      <Select value={form.term_days} onValueChange={(v) => setForm({...form, term_days: v})}>
                        <SelectTrigger className="mt-1 bg-white/5 border-white/10" data-testid="term-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="15">15 days</SelectItem>
                          <SelectItem value="30">30 days</SelectItem>
                          <SelectItem value="45">45 days</SelectItem>
                          <SelectItem value="60">60 days</SelectItem>
                          <SelectItem value="90">90 days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Preferred Currency (Optional)</Label>
                      <Select value={form.preferred_stablecoin} onValueChange={(v) => setForm({...form, preferred_stablecoin: v})}>
                        <SelectTrigger className="mt-1 bg-white/5 border-white/10" data-testid="currency-select">
                          <SelectValue placeholder="Lender decides" />
                        </SelectTrigger>
                        <SelectContent>
                          {stablecoins.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Additional Notes (Optional)</Label>
                    <Textarea value={form.additional_notes} onChange={(e) => setForm({...form, additional_notes: e.target.value})}
                      className="mt-1 bg-white/5 border-white/10" rows={2} placeholder="Any context for lenders..." />
                  </div>
                </div>
              </div>

              {/* Pledge Receivables */}
              <div className="p-6 rounded-xl bg-white/[0.03] border border-white/5">
                <h3 className="font-semibold mb-1 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-400" /> Pledge Accounts Receivable
                </h3>
                <p className="text-sm text-gray-400 mb-4">Select invoices to use as collateral</p>
                {receivables.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4 text-center">No available receivables. <button type="button" onClick={() => navigate('/business/receivables')} className="text-blue-400 underline">Add some first</button></p>
                ) : (
                  <div className="space-y-2">
                    {receivables.map(r => (
                      <label key={r.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedAR.includes(r.id) ? 'border-blue-500/40 bg-blue-500/5' : 'border-white/5 hover:border-white/10'
                      }`} data-testid={`pledge-ar-${r.id}`}>
                        <input type="checkbox" checked={selectedAR.includes(r.id)} onChange={() => toggleAR(r.id)}
                          className="rounded border-white/20" />
                        <div className="flex-1">
                          <span className="font-medium">{r.debtor_name}</span>
                          <span className="text-xs text-gray-500 ml-2">#{r.invoice_number}</span>
                        </div>
                        <span className="font-semibold">${r.invoice_amount.toLocaleString()}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Pledge Assets */}
              <div className="p-6 rounded-xl bg-white/[0.03] border border-white/5">
                <h3 className="font-semibold mb-1 flex items-center gap-2">
                  <Package className="w-5 h-5 text-purple-400" /> Pledge Tangible Assets
                </h3>
                <p className="text-sm text-gray-400 mb-4">Select business assets to strengthen your loan</p>
                {assets.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4 text-center">No available assets. <button type="button" onClick={() => navigate('/business/receivables')} className="text-blue-400 underline">Add some first</button></p>
                ) : (
                  <div className="space-y-2">
                    {assets.map(a => (
                      <label key={a.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedAssets.includes(a.id) ? 'border-purple-500/40 bg-purple-500/5' : 'border-white/5 hover:border-white/10'
                      }`} data-testid={`pledge-asset-${a.id}`}>
                        <input type="checkbox" checked={selectedAssets.includes(a.id)} onChange={() => toggleAsset(a.id)}
                          className="rounded border-white/20" />
                        <div className="flex-1">
                          <span className="font-medium">{a.description}</span>
                          <span className="text-xs text-gray-500 ml-2 capitalize">{a.asset_type}</span>
                        </div>
                        <span className="font-semibold">${a.estimated_value.toLocaleString()}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <Button type="submit" disabled={submitting || (selectedAR.length === 0 && selectedAssets.length === 0)}
                className="w-full h-12 bg-gradient-to-r from-blue-500 to-purple-500 text-lg"
                data-testid="submit-loan-request-btn">
                {submitting ? 'Submitting...' : <><Send className="w-5 h-5 mr-2" /> Submit Loan Request</>}
              </Button>
            </form>
          </div>

          {/* Sidebar — Collateral Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 space-y-4">
              <div className="p-5 rounded-xl bg-white/[0.03] border border-white/5">
                <h4 className="font-semibold mb-4">Collateral Summary</h4>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Selected Receivables</span>
                    <span>{selectedAR.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">AR Value</span>
                    <span>${receivables.filter(r => selectedAR.includes(r.id)).reduce((s, r) => s + r.invoice_amount, 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Selected Assets</span>
                    <span>{selectedAssets.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Asset Value</span>
                    <span>${assets.filter(a => selectedAssets.includes(a.id)).reduce((s, a) => s + a.estimated_value, 0).toLocaleString()}</span>
                  </div>
                  <div className="border-t border-white/5 pt-3">
                    <div className="flex justify-between font-semibold">
                      <span>Total Collateral</span>
                      <span>${totalCollateral.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Max Loan (90%)</span>
                    <span className="text-green-400 font-medium">${maxLoan.toLocaleString()}</span>
                  </div>
                  {form.amount && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Advance Rate</span>
                      <span className={`font-medium ${parseFloat(advanceRate) > 90 ? 'text-red-400' : parseFloat(advanceRate) > 80 ? 'text-yellow-400' : 'text-green-400'}`}>
                        {advanceRate}%
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 rounded-xl bg-blue-500/5 border border-blue-500/10">
                <p className="text-xs text-blue-300 leading-relaxed">
                  <strong>How it works:</strong> Submit your request with pledged collateral. Lenders will review your AR quality, business health, and AI credit analysis, then bid with their preferred interest rate and settlement currency.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
